import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { DiagramEngine, PortWidget } from "@projectstorm/react-diagrams";
import Draggable, { DraggableCore, DraggableData, DraggableEvent, DraggableEventHandler } from "react-draggable";
import { forEach, update } from "lodash";
import PortGreenEnabled from "../../../assets/images/port-green.svg";
import PortGreyEnabled from "../../../assets/images/port-grey.svg";
import PortRed from "../../../assets/images/port-red.svg";
import PortYellowEnabled from "../../../assets/images/port-yellow.svg";
import PortMarketplaceEnabled from "../../../assets/images/on-demand-marketplace-port.svg";
import PortMMREnabled from "../../../assets/images/mmr-port.svg";
import PortWhite from "../../../assets/images/port-white.svg";
import { NODE_CONSTANTS, STATUS_TYPE } from "../../../utils/appConstants";
import BandwidthLinkModel from "./CustomLink/BandwidthLink/BandwidthLinkModel";
import ContextMenu from "../../DesignSystem/context_menu";
import { PortDetails } from "../../PortDetails";
import { adjustBandwidthPosition, forceDraggablePortMove } from "./DraggableUtil";
import { MathNearestTenth } from "../../../utils/methods";

interface PortProps {
  port: any;
  engine: DiagramEngine;
  portModel?: any;
  draggable?: "not-allowed";
  isExpanded?: boolean;
  debouncePorts?: boolean;
  isBandwidth?: boolean;
  isGroup?: boolean;
  boundsRef: HTMLDivElement;
}

const Port: React.FC<PortProps> = ({ port, engine, portModel, draggable, isExpanded, debouncePorts, isBandwidth, isGroup, boundsRef }) => {
  const organizationId = parseInt((localStorage.getItem("org_id") && localStorage.getItem("org_id")) || "0");
  const portRef = React.useRef<HTMLDivElement>(null);
  const isRedOrWhitePort = useMemo(() => {
    if (
      ((port.status === STATUS_TYPE.FOR_SALE_PORT || port.status === STATUS_TYPE.FOR_SALE_BW) && port.organizationId !== organizationId) ||
      (port.status === STATUS_TYPE.BOUGHT_PORT && !port.purchasePortId) ||
      (port.status === STATUS_TYPE.BOUGHT_BW && port.current_owner_organization_id !== organizationId)
    ) {
      return true;
    }
    return false;
  }, [port]);

  const pendingMoveToWX = useMemo<boolean>(() => !!(!port.verified && port.requested_verification), [port]);
  const [isMoving, setIsMoving] = useState(false);
  const [disabled, setDisabled] = useState(isRedOrWhitePort);

  const countCrossConnects = React.useCallback(() => {
    let count = 0;
    try {
      forEach(portModel.getLinks(), (link: any) => {
        if (!(link instanceof BandwidthLinkModel)) {
          if (link.renderedPaths && link.renderedPaths.length > 0) {
            count++;
          }
        }
      });
    } catch (e: any) {
      // return nothing
    }
    return count;
  }, [portModel]);

  useEffect(() => {
    if (!isRedOrWhitePort) {
      const disable = countCrossConnects() > 0;
      setDisabled(disable);
    }
  }, [countCrossConnects()]);

  // Re-update the coords because it does not work.
  React.useEffect(() => {
    if (!isMoving) {
      updateCoords();
    }
  }, [isMoving]);

  useEffect(() => {
    updateCoords();
  }, [debouncePorts, boundsRef.clientWidth, boundsRef.clientHeight]);

  const PortIcon = useMemo(() => {
    if ((port.status === STATUS_TYPE.FOR_SALE_PORT || port.status === STATUS_TYPE.FOR_SALE_BW) && port.organizationId !== organizationId) {
      return PortWhite;
    }
    if (port.status === STATUS_TYPE.FOR_SALE_PORT || port.status === STATUS_TYPE.FOR_SALE_BW) {
      return PortYellowEnabled;
    }
    if (
      (port.status === STATUS_TYPE.BOUGHT_PORT && !port.purchasePortId) ||
      (port.status === STATUS_TYPE.BOUGHT_BW && port.current_owner_organization_id !== organizationId)
    ) {
      return PortRed;
    }
    if ((port.status === STATUS_TYPE.BOUGHT_PORT || port.status === STATUS_TYPE.BOUGHT_BW) && port.purchasePortId) {
      return PortGreenEnabled;
    }
    if (port.verified) {
      return PortGreenEnabled;
    }
    if (!(port.position_in_mmr === null || port.position_in_mmr === undefined)) {
      return PortMMREnabled;
    } else {
      return PortGreyEnabled;
    }
  }, [port]);

  const handleDragStart = React.useCallback((e: DraggableEvent, position: DraggableData) => {
    e.stopPropagation(); // To prevent drag effect on canvas so X,Y not move
    setIsMoving(true);
  }, []);

  const updateCoords = React.useCallback(() => {
    try {
      const coords = engine?.getPortCoords(portModel);
      portModel.updateCoords(coords);
    } catch (ex) {
      // console.log("updateCoords port.tsx");
    }
  }, [portModel, engine]);

  const handleDrag = React.useCallback((e: DraggableEvent, position: DraggableData) => {
    e.stopPropagation(); // To prevent drag effect
    updateCoords();
  }, []);

  const handleDragStop = React.useCallback(
    (e: DraggableEvent, position: DraggableData) => {
      e.stopPropagation();
      port.x = MathNearestTenth(position.x);
      port.y = MathNearestTenth(position.y);
      if (isBandwidth) {
        adjustBandwidthPosition(port, boundsRef);
      }

      engine?.fireEvent({}, "onCanvasMove");
      setIsMoving(false);
    },
    [isBandwidth, engine]
  );

  const tooltipTitle = useMemo(() => {
    const farendNE = port.node_name;
    const textTooltip = port.service_description ? `${port.service_description}` : `${port.port_description} ${port.port_name}`;
    return (
      <div>
        <div>
          <div>{textTooltip}</div>
          <div>{farendNE && `To: ${farendNE}`}</div>
          <div>{pendingMoveToWX && "Move To WX Pending..."}</div>
          <div style={{ fontWeight: 700 }}>{port.seller_organization && `Bought from ${port.seller_organization}`}</div>
        </div>
      </div>
    );
  }, [port]);

  const css = useMemo(() => {
    let conditionalCSS = "";
    let tooltipCSS = "tooltip";
    if (draggable !== "not-allowed") {
      if (isMoving) {
        conditionalCSS = "no-select grabbed";
        tooltipCSS = "no-select tooltip hide-tooltip";
      } else {
        conditionalCSS = "no-select grab";
        tooltipCSS = "no-select tooltip";
      }
    }
    if (isExpanded) {
      conditionalCSS += " port-in-group";
    }
    return { tooltipCSS, conditionalCSS };
  }, [isMoving, isExpanded]);

  let portContent;
  portContent = (
    <Tooltip
      classes={{
        tooltip: css.tooltipCSS,
      }}
      title={tooltipTitle}
      arrow
      placement="bottom"
      disableInteractive
      disableTouchListener
    >
      <div className={`port-item ${css.conditionalCSS}`}>
        <img className={`${pendingMoveToWX && "port-highlight"}`} src={!pendingMoveToWX ? PortIcon : ""} alt="" />
        {engine && portModel && (
          <PortWidget className={`allow-propagation ${disabled ? "hide-port" : "show-port no-cursor"}`} engine={engine} port={portModel} />
        )}
      </div>
    </Tooltip>
  );

  let style = {};
  if (!isGroup) {
    style = { display: "inline-block", position: "absolute", top: 0, left: 0 };
  }

  portContent = (
    <div className={`port-root ${isMoving ? "port-zindex" : ""}`} style={style} ref={portRef}>
      <ContextMenu
        content={portContent}
        menuItems={
          <PortDetails
            nrId={port.portId}
            refreshIndex={() => engine?.fireEvent({}, "refetchListener")}
            otherNrId={port.farPortId}
            key={0}
            onCloseContextMenu={() => {}}
          />
        }
      />
    </div>
  );
  if (draggable !== "not-allowed") {
    portContent = (
      <Draggable
        position={{ x: port.x, y: port.y }}
        cancel=".no-cursor"
        bounds={"parent"}
        onStart={handleDragStart}
        onDrag={handleDrag}
        onStop={handleDragStop}
        scale={(engine?.getModel().getZoomLevel() || 100) / 100}
      >
        {portContent}
      </Draggable>
    );
  }

  if ((port.status === STATUS_TYPE.FOR_SALE_PORT || port.status === STATUS_TYPE.FOR_SALE_BW) && port.organizationId !== organizationId) return <div />; // REMOVE WHITE LINKS PORTS

  return portContent;
};
export default Port;
